import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';

interface Props {
  storeId: string;
  onDeleted: () => void;
}

export default function DeleteStoreConfirm({ storeId, onDeleted }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [ackUnreliable, setAckUnreliable] = useState(false);

  const previewQ = useQuery({
    queryKey: ['delete-preview', storeId],
    queryFn: () => api.deleteStorePreview(storeId),
    enabled: expanded,
  });

  const deleteM = useMutation({
    mutationFn: () => api.deleteStore(storeId),
    onSuccess: () => {
      // Cascade now runs in the background. Close the dialog immediately; the
      // yellow "deleting…" pill in StoreSelector indicates progress. (No toast
      // library available in this codebase yet — console + pill suffice for v1.)
      console.info(`[Wave 8] Deleting ${storeId}… cascade running in background.`);
      onDeleted();
    },
  });

  const errMsg = deleteM.error
    ? deleteM.error instanceof HttpError
      ? deleteM.error.detail
      : (deleteM.error as Error).message
    : null;

  return (
    <div className="mt-8 pt-6 border-t border-ff-danger/30">
      <h3 className="text-sm font-semibold text-ff-danger mb-2">Danger zone</h3>

      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-ff-danger text-sm hover:underline"
          data-testid="delete-store-open"
        >
          Delete this store…
        </button>
      )}

      {expanded && (
        <div
          className="rounded-xl border border-ff-danger/30 bg-ff-danger-soft p-4"
          data-testid="delete-store-panel"
        >
          <p className="text-sm text-ff-danger font-medium mb-2">
            This will permanently delete this store and all artifacts not used
            by any other active store.
          </p>

          {previewQ.isLoading && (
            <p className="text-xs text-ff-danger">Computing scope…</p>
          )}
          {previewQ.data?.counts_available === false && (
            <p
              className="text-xs font-semibold text-ff-danger bg-white border border-ff-danger/30 rounded-lg px-2 py-1.5 mb-2"
              data-testid="delete-store-counts-warning"
            >
              ⚠ Deletion scope could NOT be computed (a source/BigQuery error).
              The counts below are unreliable — the real cascade may delete much
              more than shown. Retry to get accurate counts, or tick the box below
              to proceed anyway.
            </p>
          )}
          {previewQ.data && (
            <ul
              className="text-xs text-ff-danger mb-3 list-disc pl-5 space-y-0.5"
              data-testid="delete-store-preview-list"
            >
              <li>{previewQ.data.n_configs} design configs</li>
              <li>{previewQ.data.n_assets} uploaded assets</li>
              <li>{previewQ.data.n_cutouts_exclusive} cutout objects</li>
              <li>{previewQ.data.n_renders_exclusive} render objects</li>
              <li>{previewQ.data.n_catalog_rows} BQ catalog rows</li>
              <li>
                {previewQ.data.n_approval_rows + previewQ.data.n_override_rows} BQ
                override/approval rows
              </li>
            </ul>
          )}

          {previewQ.data?.counts_available === false && (
            <label className="flex items-start gap-2 text-xs text-ff-danger mb-3">
              <input
                type="checkbox"
                checked={ackUnreliable}
                onChange={(e) => setAckUnreliable(e.target.checked)}
                data-testid="delete-store-ack-unreliable"
                className="mt-0.5"
              />
              I understand the deletion scope could not be verified and want to
              proceed anyway.
            </label>
          )}

          <label className="block text-xs text-ff-danger mb-1">
            Type <code className="font-mono">{storeId}</code> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full border border-ff-danger/40 rounded-xl px-3 py-2 text-sm font-data mb-3 bg-white focus:outline-none focus:ring-4 focus:ring-ff-danger/15"
            data-testid="delete-store-confirm-input"
            autoComplete="off"
            spellCheck={false}
          />

          {errMsg && (
            <p className="text-xs text-ff-danger mb-2" data-testid="delete-store-error">
              {errMsg}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                setConfirmText('');
              }}
              className="ff-btn-ghost px-3 py-1 text-sm"
              data-testid="delete-store-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => deleteM.mutate()}
              disabled={
                confirmText !== storeId ||
                deleteM.isPending ||
                // REL-B-3: if the server couldn't compute deletion scope, require
                // an explicit acknowledgement before the irreversible cascade.
                (previewQ.data?.counts_available === false && !ackUnreliable)
              }
              className="ff-btn-danger bg-ff-danger px-3 py-1 text-sm text-white hover:bg-ff-danger/90 disabled:opacity-40"
              data-testid="delete-store-confirm-button"
            >
              {deleteM.isPending ? 'Deleting…' : 'Delete forever'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
